import { useEffect, useMemo, useState } from "react";
import { Timestamp } from "firebase/firestore";

// Local
import BaseModal from "../BaseModal";
import * as strings from "../../strings";
import BaseButtonSecondary from "../BaseButtonSecondary";
import { ExistingStiltInvoice } from "../../models/invoice";
import { ExistingStiltPayment } from "../../models/stilt-payment";
import InvoiceSummary, { AUTHEDPaymentsMade } from "./InvoiceSummary";
import { DbRead, hitAcctSync } from "../../database";
import LoadingSpinner from "../LoadingSpinner";
import InvoiceHeader from "./InvoiceHeader";
import { ExistingTask } from "../../models/task";
import {
  ExistingCustomer,
  getBillingInfoFromCustomerAndLocations,
} from "../../models/customer";
import { ExistingCustomerLocation } from "../../models/customer-location";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { convertToReadableTimestampDate } from "../../assets/js/convertToReadableTimestamp";
import { ExistingCraftRecord } from "../../models/craft-record";
import { generatePDF } from "../Invoices/generatePDF";
import { useUserPermissionsStore } from "../../store/user-permissions";
import { logger } from "../../logging";
import { getRoundedCurrency } from "../../utils";
import {
  getStiltLineItemFormData,
  QuickbooksSyncStatus,
} from "./ViewInvoiceDialog";
import CompareInvoiceDialog from "../Quickbooks/CompareInvoiceDialog";
import { createToastMessageID } from "../../utils/createToastMessageID";
import { useToastMessageStore } from "../../store/toast-messages";

interface Props {
  // DIALOG BASICS
  isOpen: boolean;
  onClose: () => void;
  // DATA
  siteKeyID: string;
  invoice: ExistingStiltInvoice;
  merchantName: string;
  merchantLogoURL: string | null;
  userIsSiteAdmin: boolean;
  userDisplayNamesMap: Record<string, string>;

  children: {
    /** Optional. Layout will adjust accordingly. */
    comparisonInvoice: React.ReactNode;
  };
}

/** Simplified = not much functionality */
export default function ViewSimplifiedInvoiceDialog(props: Props): JSX.Element {
  const { addToastMessage: addMessage } = useToastMessageStore();
  const { siteKeyDoc } = useSiteKeyDocStore();
  const { siteKeyUserPermissions: userPermissions } = useUserPermissionsStore();

  const [isLoading, setIsLoading] = useState(true);
  const [payments, setPayments] = useState<ExistingStiltPayment[] | null>(null);

  const [isCompareDialogOpen, setIsCompareDialogOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [task, setTask] = useState<ExistingTask | null>(null);
  const [workRecord, setWorkRecord] = useState<ExistingCraftRecord | null>(
    null,
  );
  const [customer, setCustomer] = useState<ExistingCustomer | null>(null);
  const [cLocation, setCLocation] = useState<ExistingCustomerLocation | null>(
    null,
  );

  const isQuickbooksEnabled: boolean = useMemo(() => {
    return (
      siteKeyDoc?.customizations?.accounting?.pushToCodat ||
      siteKeyDoc?.customizations?.accounting?.pushToConductor ||
      siteKeyDoc?.customizations?.accounting?.pushToQBO
    );
  }, [siteKeyDoc]);

  useEffect(() => {
    async function fetchData() {
      let task: ExistingTask | null = null;
      let parentRecord: ExistingCraftRecord | null = null;
      try {
        task = props.invoice.taskID
          ? await DbRead.tasks.getDocByID(props.siteKeyID, props.invoice.taskID)
          : null;
      } catch (error) {
        logger.error("Error fetching task", error);
      }
      try {
        parentRecord = props.invoice.craftRecordID
          ? await DbRead.parentRecords.get(
              props.siteKeyID,
              props.invoice.craftRecordID,
            )
          : null;
      } catch (error) {
        logger.error("Error fetching parentRecord", error);
      }

      const morePromises = [
        DbRead.customers.get(props.siteKeyID, props.invoice.customerID),
        DbRead.customerLocations.getSingle(
          props.siteKeyID,
          props.invoice.customerLocationID,
        ),
      ] as const;
      const [customer, customerLocation] = await Promise.all(morePromises);

      setTask(task);
      setWorkRecord(parentRecord);
      setCustomer(customer);
      setCLocation(customerLocation);
    }

    fetchData();
  }, [props.invoice, props.siteKeyID]);

  useEffect(() => {
    function getPayments() {
      return DbRead.payments.subscribeAllByInvoiceID({
        siteKey: props.siteKeyID,
        invoiceID: props.invoice.id,
        onChange: setPayments,
        onError: logger.error,
      });
    }
    const unsubscribe = getPayments();
    return () => unsubscribe && unsubscribe();
  }, [props.invoice.id, props.siteKeyID]);

  useEffect(() => {
    if (!payments) return; // reminder to self - [] won't return early
    setIsLoading(false);
  }, [payments]);

  function closeDialog() {
    setPayments([]);
    setTask(null);
    setCustomer(null);
    setCLocation(null);
    props.onClose();
  }

  let subTotalWithoutGlobalDiscount = 0;
  let allDiscountsApplied = 0;
  props.invoice.lineItems.forEach((i) => {
    subTotalWithoutGlobalDiscount += i.subTotal;
    // Add in line discounts
    allDiscountsApplied += i.quantity * i.unitPrice - i.subTotal;
  });
  // Add in global discount
  allDiscountsApplied += subTotalWithoutGlobalDiscount - props.invoice.subTotal;

  async function handleGetPDF(invoiceID: string) {
    if (!siteKeyDoc) return;
    await generatePDF.invoice(
      siteKeyDoc,
      [invoiceID],
      true,
      siteKeyDoc?.customizations.defaultIncludeJobPhotos ?? false,
      userPermissions,
    );
  }

  async function handleManualSync() {
    setIsSyncing(true);
    try {
      const result = await hitAcctSync({
        siteKeyID: props.siteKeyID,
        refPathList: [props.invoice.refPath],
      });

      if (result && result.length > 0) {
        addMessage({
          id: createToastMessageID(),
          message: `Failed to sync invoice ${props.invoice.invoiceNumber}. Please try again later.`,
          type: "error",
          dialog: true,
        });
      } else {
        addMessage({
          id: createToastMessageID(),
          message: `Successfully initiated sync for invoice ${props.invoice.invoiceNumber}.`,
          type: "success",
          dialog: true,
        });
      }
    } catch (error) {
      logger.error(
        `(handleManualSync) Error syncing invoice ${props.invoice.invoiceNumber}`,
        error,
      );
      addMessage({
        id: createToastMessageID(),
        message: `Error syncing invoice ${props.invoice.invoiceNumber}. Please try again later.`,
        type: "error",
        dialog: true,
      });
    } finally {
      setIsSyncing(false);
    }
  }

  // const totalAmountPaid = payments?.reduce((acc, p) => acc + p.amount, 0) ?? 0;
  // boomer loop is more performant than .reduce
  let totalAmountPaid = 0;
  for (const p of payments ?? []) totalAmountPaid += p.amount;
  totalAmountPaid = getRoundedCurrency(totalAmountPaid);

  // doesn't matter if the user is site admin or not. they're authed, and that's what matters here
  const paymentsSection = siteKeyDoc && payments && payments.length > 0 && (
    <AUTHEDPaymentsMade
      paymentsMade={payments}
      currency={siteKeyDoc.customizations.accounting?.currency ?? "USD"}
      timezone={siteKeyDoc.timezone ?? "America/New_York"}
      siteKeyID={props.siteKeyID}
      handleRefund={null}
      handleDelete={null}
      userDisplayNamesMap={props.userDisplayNamesMap}
    />
  );

  const invoiceSummary = props.userIsSiteAdmin && siteKeyDoc && (
    <InvoiceSummary
      shouldShowAuthedVersion={true}
      siteKey={siteKeyDoc.id}
      siteFilterPbiByLocationID={
        siteKeyDoc.customizations.filterPriceBookItemsByLocationID ?? false
      }
      totalTaxRate={cLocation?.totalTaxRate ?? 0}
      customer={customer}
      currency={siteKeyDoc.customizations.accounting?.currency ?? "USD"}
      items={getStiltLineItemFormData(props.invoice)}
      totalTaxAmount={props.invoice.totalTaxAmount}
      totalTaxAmountPST={props.invoice.totalTaxAmountPST}
      totalTaxAmountGST={props.invoice.totalTaxAmountGST}
      subTotalWithoutGlobalDiscount={subTotalWithoutGlobalDiscount}
      subTotalWithGlobalDiscount={props.invoice.subTotal}
      allDiscountsApplied={allDiscountsApplied}
      totalAmount={props.invoice.totalAmount}
      amountDue={props.invoice.amountDue}
      handlePayment={null}
      note={props.invoice.note}
      discount={props.invoice.discount}
      internalNote={props.invoice.internalNotes}
      lineItemsUpdated={null}
      hasTask={false}
      tipsEnabled={false}
      totalAmountPaid={totalAmountPaid}
      children={{
        editItemsButton: null,
        paymentsMadeSection: paymentsSection,
      }}
      tipAmount={0}
      allowEdit={false}
      isMembershipInvoice={false} // doesn't matter in the simplified view
      isSavingGlobalDiscount={false}
      updateGlobalDiscount={null}
      locationID={props.invoice.locationID}
      invoiceID={props.invoice.id}
    />
  );

  const invoiceSummaryNonAdmin = !props.userIsSiteAdmin && siteKeyDoc && (
    <InvoiceSummary
      shouldShowAuthedVersion={false}
      totalTaxRate={cLocation?.totalTaxRate ?? 0}
      currency={siteKeyDoc.customizations.accounting?.currency ?? "USD"}
      items={getStiltLineItemFormData(props.invoice)}
      totalTaxAmount={props.invoice.totalTaxAmount}
      totalTaxAmountPST={props.invoice.totalTaxAmountPST}
      totalTaxAmountGST={props.invoice.totalTaxAmountGST}
      subTotalWithoutGlobalDiscount={subTotalWithoutGlobalDiscount}
      subTotalWithGlobalDiscount={props.invoice.subTotal}
      allDiscountsApplied={allDiscountsApplied}
      totalAmount={props.invoice.totalAmount}
      amountDue={props.invoice.amountDue}
      note={props.invoice.note}
      discount={props.invoice.discount}
      tipsEnabled={false}
      totalAmountPaid={totalAmountPaid}
      children={{ tipsSection: null, paymentsMadeSection: paymentsSection }}
      tipAmount={props.invoice.tipAmount ?? 0}
    />
  );

  const invoiceHeader = customer && cLocation && (
    <InvoiceHeader
      dueDate={parseDate(props.invoice.dueDate) ?? "[unknown]"}
      issueDate={parseDate(props.invoice.issueDate) ?? "[unknown]"}
      poNumber={props.invoice.poNumber}
      jobNumber={workRecord?.craftDetails.jobNumber ?? null}
      invoiceNumber={props.invoice.invoiceNumber}
      invoiceStatus={props.invoice.status}
      amountDue={props.invoice.amountDue}
      invoicePaymentTerms={props.invoice.paymentTerms ?? null}
      merchantLogoURL={props.merchantLogoURL}
      timestampTaskCompleted={task?.timestampTaskCompleted ?? null}
      billingInfo={getBillingInfoFromCustomerAndLocations(customer, [
        cLocation,
      ])}
      serviceInfo={{
        addressLine1: cLocation.addressLine1 ?? "",
        addressLine2: cLocation.addressLine2 ?? "",
        city: cLocation.city ?? "",
        state: cLocation.state ?? "",
        zipCode: cLocation.zipCode ?? "",
      }}
      sitePaymentTerms={siteKeyDoc?.customizations.paymentTerms}
      craftRecordID={props.invoice.craftRecordID}
      customerID={props.invoice.customerID}
      estimateID={props.invoice.estimateID}
      customerLocationID={props.invoice.customerLocationID}
      invoiceID={props.invoice.id}
      getPDF={handleGetPDF}
    />
  );

  return (
    <BaseModal
      open={props.isOpen}
      closeModal={closeDialog}
      title={<></>}
      parentDivStyles="text-left max-w-4xl"
    >
      <div className="relative p-6 text-gray-900 md:p-8">
        {isLoading ? (
          <LoadingSpinner sizeClass="h-8 w-8" marginClass="mx-auto" />
        ) : (
          <>
            {props.userIsSiteAdmin && isQuickbooksEnabled && (
              <QuickbooksSyncStatus
                invoice={props.invoice}
                isQuickbooksEnabled={isQuickbooksEnabled}
                handleManualSync={handleManualSync}
                isSyncing={isSyncing}
                setIsCompareInvoiceDialogOpen={setIsCompareDialogOpen}
                disableFunctionality={props.children.comparisonInvoice != null}
              />
            )}
            {invoiceHeader}

            {props.userIsSiteAdmin ? invoiceSummary : invoiceSummaryNonAdmin}

            {props.children.comparisonInvoice && (
              <div className="mb-4">{props.children.comparisonInvoice}</div>
            )}

            {/* <div className="mt-4 flex justify-end"> */}
            <BaseButtonSecondary
              type="button"
              className="w-full uppercase sm:w-44"
              onClick={closeDialog}
            >
              {strings.buttons.CLOSE}
            </BaseButtonSecondary>
          </>
        )}
      </div>

      {props.userIsSiteAdmin && props.children.comparisonInvoice == null && (
        <CompareInvoiceDialog
          isOpen={isCompareDialogOpen}
          onClose={() => setIsCompareDialogOpen(false)}
          siteKey={props.siteKeyID}
          stiltInvoice={props.invoice}
          currency={siteKeyDoc?.customizations.accounting?.currency ?? "USD"}
        />
      )}
    </BaseModal>
  );
}

function parseDate(ts: Timestamp | null): string | null {
  if (!ts) return null;
  try {
    return convertToReadableTimestampDate(ts);
  } catch (_e) {
    return null;
  }
}
